import { compareAsc, parseISO } from "date-fns"

import {
  CONVENIENCE_FEE_FUNDING_METHOD,
  REFETCHABLE_INVOICE_PAYMENT_METHODS,
  REFETCHABLE_INVOICE_STATUSES,
} from "./constants"

export const getRequestPaymentMethodId = (paymentMethodId) => {
  switch (paymentMethodId) {
    case "manual":
    case "":
    case undefined:
      return null
    default:
      return Number(paymentMethodId)
  }
}

export const getFirstOfNextMonth = () => {
  const today = new Date()
  const nextMonth = today.getMonth() + 1
  const year = nextMonth === 12 ? today.getFullYear() + 1 : today.getFullYear()
  return new Date(year, nextMonth % 12, 1)
}

export const groupByRecurringProductSaleId = (obj, item) => {
  const recurringProductSaleId =
    item.product_sale.recurring_product_sale?.id || null

  obj[recurringProductSaleId] = obj[recurringProductSaleId] || []
  obj[recurringProductSaleId].push(item)

  return obj
}

export const hasDisputedInvoice = (invoices) => {
  return invoices?.some((invoice) => isDisputed(invoice))
}

export const hasMultipleUnsettledInvoices = (invoices = []) => {
  return invoices.filter((invoice) => invoice.balance !== 0).length > 1
}

export const invoiceIsMergeable = (invoice) => {
  // note: this isn't perfect because it doesn't check if the invoice has
  // any reversed payments. The invoice doesn't have access to the txns
  // so we'd need to send more data. We will still be protected on the backend

  return ["Scheduled", "Past Due"].includes(invoice?.display_status)
}

export const isDisputed = (invoice) => {
  return invoice?.display_status === "Disputed"
}

// https://stackoverflow.com/a/31732581
// TLDR: JS handles dates weirdly, if you use dash-separated
// dates you can end up a day off VS using slash separations
export const parseDateValue = (dateString) => {
  return parseISO(dateString, "yyyy-MM-dd", new Date())
}

export const isLostDispute = (invoiceTxn) => {
  return invoiceTxn?.txn?.stripe_payment?.status === "twg_dispute_lost"
}

export const isNonSucceededStripePayment = (invoiceTxn) => {
  return (
    invoiceTxn?.txn?.stripe_payment &&
    invoiceTxn.txn.stripe_payment.status !== "succeeded"
  )
}

const getProductSaleOrReturn = (i) => {
  if ("txn" in i) {
    return i.txn?.product_sale ?? i.txn?.product_return
  }
  return i.product_sale ?? i?.product_return
}

export const sortItems = (items = []) => {
  if (!items?.length) return

  const itemsToSort = [...items]
  const reservationSaleIndex = items.findIndex(
    (i) => getProductSaleOrReturn(i).reservation_sale
  )

  let reservationSale = null
  if (reservationSaleIndex >= 0) {
    ;[reservationSale] = itemsToSort.splice(reservationSaleIndex, 1)
  }

  const sortedItems = itemsToSort.sort(sortByServiceStartDateAndName)
  const result = reservationSale
    ? [reservationSale, ...sortedItems]
    : sortedItems
  return result
}

export const sortRecurringItems = (items = []) => {
  if (!items?.length) return

  return items.sort(sortByServiceStartDateAndName)
}

const sortByServiceStartDateAndName = (a, b) => {
  const productSaleA = getProductSaleOrReturn(a)
  const productSaleB = getProductSaleOrReturn(b)

  const parsedDateA = parseDateValue(
    productSaleA?.service_start_date ??
      productSaleA?.product_sale_service_start_date
  )
  const parsedDateB = parseDateValue(
    productSaleB?.service_start_date ??
      productSaleB?.product_sale_service_start_date
  )
  const dateCompare = compareAsc(parsedDateA, parsedDateB)
  if (dateCompare === 0) {
    return (
      productSaleA?.name ?? productSaleA?.product_sale_name
    )?.localeCompare(productSaleB?.name ?? productSaleB?.product_sale_name)
  }
  return dateCompare
}

export const getDatePortionOfISODate = (isoDateString) => {
  return isoDateString.split("T")[0]
}

export const invoicesIncludeProcessingCardPayments = (invoices) => {
  return invoices.some((invoice) => {
    // We only want to continuously refetch if there is a payment in a "Processing" state.
    const isProcessing = REFETCHABLE_INVOICE_STATUSES.includes(
      invoice.display_status
    )
    // ACH payments can be in a "Processing" state for many days,
    // so we only want to continuously refetch if it is a card payment.
    const isCardPayment = REFETCHABLE_INVOICE_PAYMENT_METHODS.includes(
      invoice.payment_method?.type
    )
    return isProcessing && isCardPayment
  })
}

export const getAreDatesEqual = (a, b) => {
  return [a, b]
    .map((date) => getDatePortionOfISODate(date.toISOString()))
    .every((v, _i, arr) => v === arr[0])
}

export const paymentMethodIsCreditCard = (
  cardAttribute,
  cardAttributeValue,
  paymentMethods = []
) => {
  const selectedPaymentMethod = paymentMethods.find(
    (card) => card[cardAttribute] === cardAttributeValue
  )

  return (
    selectedPaymentMethod?.metadata?.funding === CONVENIENCE_FEE_FUNDING_METHOD
  )
}
